import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { projects, projectCategories } from '../../data/projects';
import SectionTitle from '../common/SectionTitle';
import AnimatedSection from '../ui/AnimatedSection';
import TechIcon from '../ui/TechIcon';
import type { Project } from '../../data/projects';
import '../../styles/components/Projects.css';

const Projects: React.FC = () => {
  const { t, language } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeProjectId, setActiveProjectId] = useState<string>('');
  const [isContentAnimating, setIsContentAnimating] = useState(false);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const filteredProjects = useMemo(() =>
    activeFilter === 'all'
      ? projects
      : projects.filter(p => p.category === activeFilter),
    [activeFilter]
  );

  // Keep active project in sync when filter changes
  useEffect(() => {
    if (filteredProjects.length > 0) {
      setActiveProjectId(filteredProjects[0].id);
    }
  }, [activeFilter, filteredProjects]);

  // Initialize active project on mount
  useEffect(() => {
    if (projects.length > 0 && !activeProjectId) {
      setActiveProjectId(projects[0].id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sliding filter indicator
  const updateIndicator = useCallback(() => {
    if (!filterBarRef.current) return;
    const activeBtn = filterBarRef.current.querySelector('.projects__filter--active') as HTMLElement;
    if (!activeBtn) return;
    const barRect = filterBarRef.current.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    setIndicatorStyle({
      width: btnRect.width,
      transform: `translateX(${btnRect.left - barRect.left}px)`,
    });
  }, []);

  useEffect(() => {
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeFilter, updateIndicator, language]);

  const activeProject = useMemo(
    () => filteredProjects.find(p => p.id === activeProjectId) ?? filteredProjects[0] ?? null,
    [filteredProjects, activeProjectId]
  );

  const handleTabClick = useCallback((projectId: string) => {
    if (projectId === activeProjectId) return;

    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }

    setIsContentAnimating(true);

    const switchDelay = prefersReducedMotion ? 0 : 120;
    animationTimerRef.current = setTimeout(() => {
      setActiveProjectId(projectId);
      setIsContentAnimating(false);
    }, switchDelay);
  }, [activeProjectId, prefersReducedMotion]);

  useEffect(() => {
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, []);

  const getProjectName = (project: Project) =>
    language === 'vi' && project.nameVi ? project.nameVi : project.name;

  const getProjectDescription = (project: Project) =>
    language === 'vi' && project.descriptionVi ? project.descriptionVi : project.description;

  const getCategoryLabel = (category: string) => {
    const cat = projectCategories.find(c => c.id === category);
    if (!cat) return category;
    return language === 'vi' && cat.labelVi ? cat.labelVi : cat.label;
  };

  const handleFilterChange = useCallback((filterId: string) => {
    setActiveFilter(filterId);
  }, []);

  // Keyboard nav on tab list
  const handleTabKeyDown = useCallback((e: React.KeyboardEvent, projectId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTabClick(projectId);
    }
  }, [handleTabClick]);

  return (
    <section id="projects" className="section projects-section">
      <div className="container">
        <SectionTitle title={t('projects.title')} subtitle={t('projects.subtitle')} />

        {/* Filter tabs with sliding indicator */}
        <AnimatedSection className="projects__filters-wrapper">
          <div className="projects__filters" ref={filterBarRef}>
            <div className="projects__filter-indicator" style={indicatorStyle} />
            {projectCategories.map(cat => (
              <button
                key={cat.id}
                className={`projects__filter ${activeFilter === cat.id ? 'projects__filter--active' : ''}`}
                onClick={() => handleFilterChange(cat.id)}
              >
                {language === 'vi' && cat.labelVi ? cat.labelVi : cat.label}
              </button>
            ))}
          </div>
        </AnimatedSection>

        {/* Split Panel */}
        <AnimatedSection animation="fade-in-up">
          <div className="projects__split-panel">
            {/* Left: Tab List */}
            <nav
              className="projects__tab-list"
              aria-label="Project list"
              role="tablist"
            >
              {filteredProjects.map((project) => {
                const isActive = project.id === activeProjectId;
                return (
                  <button
                    key={project.id}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="projects-panel"
                    className={`projects__tab ${isActive ? 'projects__tab--active' : ''}`}
                    onClick={() => handleTabClick(project.id)}
                    onKeyDown={(e) => handleTabKeyDown(e, project.id)}
                  >
                    <span className="projects__tab-indicator" aria-hidden="true" />
                    <span className="projects__tab-inner">
                      <span className="projects__tab-category">
                        {getCategoryLabel(project.category)}
                      </span>
                      <span className="projects__tab-name">
                        {getProjectName(project)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </nav>

            {/* Right: Detail Panel */}
            <div
              id="projects-panel"
              role="tabpanel"
              className="projects__detail-panel"
              aria-live="polite"
            >
              {activeProject && (
                <div
                  key={activeProject.id}
                  className={`projects__detail-content ${isContentAnimating ? 'projects__detail-content--exit' : 'projects__detail-content--enter'}`}
                >
                  {/* Category badge */}
                  <span className="projects__detail-category">
                    {getCategoryLabel(activeProject.category)}
                  </span>

                  {/* Title */}
                  <h3 className="projects__detail-title">
                    {getProjectName(activeProject)}
                  </h3>

                  {/* Description */}
                  <p className="projects__detail-description">
                    {getProjectDescription(activeProject)}
                  </p>

                  {/* Tech stack */}
                  <div className="projects__detail-tech-section">
                    <h4 className="projects__detail-tech-label">
                      Tech Stack
                    </h4>
                    <div className="projects__detail-tech-grid">
                      {activeProject.technologies.map((tech) => (
                        <span key={tech} className="projects__detail-tech-pill">
                          <TechIcon name={tech} />
                          <span>{tech}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Projects;
